import { Suspense, useEffect, useMemo } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import routes, { AppRoute, ROUTES } from 'app/routes';
import Loading from 'components/Loading';
import './App.scss';
import PageNotFound from 'pages/page-not-found';
import AppLayout from 'layout/AppLayout';
import { useAppDispatch, useAppSelector } from './hooks';
import { getData } from 'features/organizations-data/organization-data';
import { OrganizationPublicJobBoardData } from 'features/organizations-data/types';

const PAGE_KEY_TO_ROUTE: Record<string, { route: string; isEnabled: (org: OrganizationPublicJobBoardData) => boolean }> = {
  home: { route: ROUTES.HUB_POSTS, isEnabled: org => org.isHomeEnabled !== false },
  jobs: { route: ROUTES.OPPORTUNITIES, isEnabled: org => org.isJobsEnabled !== false },
  'products-services': { route: ROUTES.PRODUCTS_SERVICES, isEnabled: org => org.isProductsServicesEnabled !== false },
  events: { route: ROUTES.EVENTS, isEnabled: org => org.isEventsEnabled !== false },
};

const resolveDefaultPage = (org: OrganizationPublicJobBoardData): string => {
  const key = org.defaultPage ?? '';
  const entry = PAGE_KEY_TO_ROUTE[key];
  if (entry && entry.isEnabled(org)) return entry.route;
  return ROUTES.PROGRAMS;
};

const App = () => {
  const dispatch = useAppDispatch();
  const { status, organization } = useAppSelector(state => state.organizationData);
  const isLoading = useMemo(() => status === 'pending', [status]);

  useEffect(() => {
    dispatch(getData());
  }, [dispatch]);

  if (isLoading) {
    return <Loading />;
  } else if (!organization) {
    return <PageNotFound />;
  }

  return (
    <AppLayout>
      <Suspense fallback={<Loading />}>
        <Routes>
          {routes.map((route: AppRoute) => {
            const { component: Component, ...rest } = route;
            return <Route key={route.path} element={<Component />} {...rest} />;
          })}
          <Route path="/" element={<Navigate to={resolveDefaultPage(organization)} replace={true} />} />
          <Route path="*" element={<PageNotFound />} />
        </Routes>
      </Suspense>
    </AppLayout>
  );
};

export default App;
